import * as types from '../store/mutation-types';
import RegistrationClient from './RegistrationClient';

const state = {
    churchName: '',
    resendToken: '',
    licenseKey: '',
    informations: {
        registerFinished: false,
        appellation: 'ms'
    },
    setupMeetings: [],
    validationErrors: {},
    showValidationErrors: {
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
        website: false,
        churchName: false,
        city: false,
        countryCode: false,
        siteName: false,
        baseLanguage: false,
        heardFrom: false,
        checkagbs: false,
        checkgdpr: false,
        newsletter: false,
        setupMeetingDate: false
    }
};

const getters = {
    registerInformation: state => {
        return state.informations;
    },

    resendToken: state => {
        return state.resendToken;
    },

    showValidationErrors: state => {
        return state.showValidationErrors;
    },

    setupMeetings: state => {
        return state.setupMeetings;
    }
};

const actions = {
    register({ commit }, { register }) {
        commit(types.RESET_SHOW_ERRORS);
        return new Promise((resolve, reject) => {
            RegistrationClient.sendRegistration(register)
                .then(result => {
                    resolve(result.data.resendToken);
                })
                .catch(error => {
                    error.response.data.errorFields.forEach(fieldError => {
                        commit(types.SET_VALIDATION_ERROR, {
                            fieldName: fieldError.fieldId,
                            message: fieldError.messageKey,
                            args: fieldError.args
                        });
                        commit(types.SHOW_VALIDATION_ERROR, fieldError.fieldId);
                    });
                    reject(error.response.data);
                });
        });
    },

    /**
     * call /api/validate/:validationToken to validate the email address and get the user object
     * @param commit
     * @param token
     * @returns {Promise}
     */
    validateEmail({ commit }, { token }) {
        return new Promise((resolve, reject) => {
            RegistrationClient.validateEmail(token, customer => {
                if (typeof customer.success !== 'undefined' && customer.success) {
                    commit(types.SET_LICENSE_KEY, customer.customer.licenseKey);
                    resolve();
                } else {
                    reject('failed to validate email with validationToken ' + token);
                }
            });
            RegistrationClient.validateEmail(
                token,
                customer => {
                    commit(types.SET_LICENSE_KEY, customer.licenseKey);
                    resolve();
                },
                error => {
                    reject(error.message);
                }
            );
        });
    },

    loadRegistration({ commit }, { token }) {
        return new Promise((resolve, reject) => {
            if (typeof state.informations.name === 'undefined') {
                RegistrationClient.getRegisterDataByToken(token, customer => {
                    commit(types.SET_REGISTER_INFORMATION, customer);
                    commit(types.SET_RESEND_TOKEN, token);
                    resolve();
                });
            } else {
                resolve();
            }
        });
    },

    newRegistration({ commit }) {
        window.localStorage.removeItem('resendToken');
        window.localStorage.removeItem('ctLicense');
        commit(types.SET_RESEND_TOKEN, '');
        commit(types.SET_REGISTER_INFORMATION, {
            registerFinished: false,
            appellation: 'ms'
        });
    },

    loadSetupMeetings({ commit }) {
        return new Promise((resolve, reject) => {
            let setupMeetings = RegistrationClient.getSetupMeetingOptions();
            setupMeetings
                .then(meetings => {
                    commit(types.SET_SETUP_MEETINGS, meetings.data);
                    resolve();
                })
                .catch(e => {
                    reject(e);
                });
        });
    },

    checkSiteName({ commit }, { siteName, denomination, inKRZ }) {
        return new Promise((resolve, reject) => {
            RegistrationClient.checkSite(siteName, denomination, inKRZ)
                .then(response => {
                    commit(types.HIDE_VALIDATION_ERROR, 'siteName');
                    resolve();
                })
                .catch(error => {
                    const errorFields = error.response.data.errorFields;
                    let fieldName;
                    let message;
                    let args;
                    if (errorFields) {
                        const errorField = errorFields[0];
                        fieldName = errorField.fieldId;
                        message = errorField.messageKey;
                        args = errorField.args;
                    } else {
                        fieldName = 'siteName';
                        message = 'validation.sitename.invalidchars';
                        args = {};
                    }
                    commit(types.SET_VALIDATION_ERROR, { fieldName: fieldName, message: message, args: args });
                    commit(types.SHOW_VALIDATION_ERROR, 'siteName');
                    reject();
                });
        });
    },

    checkSiteNameExists({ commit }, { siteName, denomination, inKRZ }) {
        return new Promise((resolve, reject) => {
            RegistrationClient.checkSiteExists(siteName, inKRZ)
                .then(response => {
                    commit(types.HIDE_VALIDATION_ERROR, 'siteName');
                    resolve();
                })
                .catch(error => {
                    const errorFields = error.response.data.errorFields;
                    let fieldName;
                    let message;
                    let args;
                    if (errorFields) {
                        const errorField = errorFields[0];
                        fieldName = errorField.fieldId;
                        message = errorField.messageKey;
                        args = errorField.args;
                    } else {
                        fieldName = 'siteName';
                        message = 'validation.sitename.invalidchars';
                        args = {};
                    }
                    commit(types.SET_VALIDATION_ERROR, { fieldName: fieldName, message: message, args: args });
                    commit(types.SHOW_VALIDATION_ERROR, 'siteName');
                    reject();
                });
        });
    }
};

const mutations = {
    [types.RESET_SHOW_ERRORS](state) {
        state.showValidationErrors = {
            firstName: false,
            lastName: false,
            email: false,
            phone: false,
            website: false,
            churchName: false,
            city: false,
            countryCode: false,
            siteName: false,
            baseLanguage: false,
            heardFrom: false,
            checkagbs: false,
            checkgdpr: false,
            newsletter: false,
            setupMeetingDate: false
        };
    },

    [types.SET_VALIDATION_ERROR](state, params) {
        state.validationErrors[params.fieldName] = { translationKey: params.message, args: params.args };
    },

    [types.HIDE_VALIDATION_ERROR](state, fieldName) {
        state.showValidationErrors[fieldName] = false;
    },

    [types.SHOW_VALIDATION_ERROR](state, fieldName) {
        state.showValidationErrors[fieldName] = true;
    },

    [types.SET_RESEND_TOKEN](state, resendToken) {
        state.resendToken = resendToken;
    },

    [types.SET_REGISTER_INFORMATION](state, data) {
        state.informations = data;
    },

    [types.SET_SETUP_MEETINGS](state, data) {
        state.setupMeetings = data;
    },

    [types.SET_LICENSE_KEY](state, license) {
        window.localStorage.removeItem('resendToken');
        window.localStorage.setItem('ctLicense', license);
        state.licenseKey = license;
    }
};

const namespaced = true;

export default {
    state,
    getters,
    actions,
    mutations,
    namespaced
};
